import React, { useMemo } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import OptionSwitch from "./OptionSwitch"
import { DOWNLOAD_TYPES, INPUTS } from "hooks/budgeting/useDownloadBudgetConfig"
import { Box, IconButton, Tooltip, Typography } from "@mui/material"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import BudgetExportSelect from "./BudgetExportSelect"

const blurActiveElement = () => {
  document.activeElement?.blur?.()
}

const BC3Options = ({
  setDownloadBudgetConfig,
  addCostsRef,
  addCosts,
  setAddCosts,
  addMeasurements,
  setAddMeasurements,
  addDecompositions,
  setAddDecompositions,
  simpleMode,
  setSimpleMode,
}) => {
  const tipo = DOWNLOAD_TYPES.BC3
  const selectAddCostsOptions = [
    { value: "false", label: i18n.t("downloadBudget.clientPrice") },
    { value: "true", label: i18n.t("downloadBudget.targetCost") },
  ]
  const options = useMemo(
    () => [
      {
        i18nKey: "downloadBudget.exportMeasurements",
        state: addMeasurements,
        setState: setAddMeasurements,
        inputKey: INPUTS.EXPORT_MEASUREMENTS,
      },
      {
        i18nKey: "downloadBudget.exportDecompositions",
        state: addDecompositions,
        setState: setAddDecompositions,
        inputKey: INPUTS.EXPORT_DECOMPOSITIONS,
      },
    ],
    [
      addMeasurements,
      setAddMeasurements,
      addDecompositions,
      setAddDecompositions,
    ],
  )

  return (
    <>
      <OptionSwitch
        key={`bc3_${INPUTS.SIMPLE_MODE}`}
        i18nKey="downloadBudget.simpleMode"
        state={simpleMode}
        setState={setSimpleMode}
        inputKey={INPUTS.SIMPLE_MODE}
        tipo={tipo}
        setDownloadBudgetConfig={setDownloadBudgetConfig}
      />

      {simpleMode && (
        <Box
          sx={{
            mt: 1,
            mb: 0.75,
            maxWidth: 320,
            width: "100%",
            px: 1.5,
            py: 1.25,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            bgcolor: "#f6f7fb",
            border: "1px solid #eee",
            borderRadius: "4px",
            boxShadow: "none",
          }}
        >
          <Tooltip title={i18n.t("downloadBudget.tooltipWarning")}>
            <IconButton
              type="button"
              size="small"
              aria-label={i18n.t("downloadBudget.tooltipWarning")}
              sx={{
                flexShrink: 0,
                alignSelf: "flex-start",
                p: 0.25,
                mt: "1px",
                color: "secondary.main",
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Typography
            variant="body2"
            component="p"
            sx={{
              m: 0,
              flex: 1,
              minWidth: 0,
              color: "#414141",
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            {i18n.t("downloadBudget.simpleModeDescription")}
          </Typography>
        </Box>
      )}

      {!simpleMode && (
        <>
          <BudgetExportSelect
            inputRef={addCostsRef}
            value={addCosts ? "true" : "false"}
            options={selectAddCostsOptions}
            onChange={event => {
              const v = event.target.value === "true"
              setAddCosts(v)
              setDownloadBudgetConfig(tipo, INPUTS.USE_COSTS, v)
              blurActiveElement()
            }}
          />
          {options.map(({ i18nKey, state, setState, inputKey }) => (
            <OptionSwitch
              key={`bc3_${inputKey}`}
              i18nKey={i18nKey}
              state={state}
              setState={setState}
              inputKey={inputKey}
              tipo={tipo}
              setDownloadBudgetConfig={setDownloadBudgetConfig}
            />
          ))}
        </>
      )}
    </>
  )
}

BC3Options.propTypes = {
  setDownloadBudgetConfig: PropTypes.func.isRequired,
  addCostsRef: PropTypes.any.isRequired,
  addCosts: PropTypes.bool.isRequired,
  setAddCosts: PropTypes.func.isRequired,
  addMeasurements: PropTypes.bool.isRequired,
  setAddMeasurements: PropTypes.func.isRequired,
  addDecompositions: PropTypes.bool.isRequired,
  setAddDecompositions: PropTypes.func.isRequired,
  simpleMode: PropTypes.bool.isRequired,
  setSimpleMode: PropTypes.func.isRequired,
}

export default BC3Options
